/**
 * `FocusPaneHost` — the reusable right-hand working pane.
 *
 * Mounted once at page scope (sibling of the main column, like
 * `WorkspaceFilePaneShell`) so it spans the full viewport height and animates
 * its width. WHICH surface it shows comes from `useUIStore.focusPane` + the
 * registry; this component owns only the chrome: open/close animation, the
 * resize handle, the compact-viewport overlay, and Esc-to-close.
 *
 * The layout choreography — collapse the nav rail, hide the file/case column
 * and the Agents pane on open, restore all three on close — lives in the store
 * (`openFocusPane`/`closeFocusPane`), NOT here, because the snapshot has to
 * survive this component unmounting mid-animation.
 *
 * Width is a persisted percentage clamped to the operator's 35–65% band: wide
 * enough that a list+detail surface is usable, never so wide the conversation
 * stops being a conversation.
 *
 * LexIDE only — there is no equivalent plane in LexPortal.
 */
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";

import {
  FOCUS_PANE_MAX_PCT,
  FOCUS_PANE_MIN_PCT,
  useUIStore,
} from "@/stores/useUIStore";
import { useActiveContextStore } from "@/stores/useActiveContextStore";
import { isCompactViewport, useViewportSize } from "@/hooks/useViewportSize";
import { useTasklistGate } from "@/hooks/useTasklistGate";
import { ResizeHandle } from "@/components/shell/ResizeHandle";
import { FOCUS_PANE_SURFACES } from "@/components/shell/focus-pane-registry";
import { chatTargetForContext, isChatLeftRoute } from "@/lib/chat-left-routes";

/** Kept in lockstep with the CSS transition below (mirrors the file pane). */
const OPEN_CLOSE_MS = 220;

/**
 * Below this PANE width a surface's list+detail split stops paying for itself —
 * the detail ends up a ~300px slice. Measured on the pane, never the viewport:
 * a `lg:` breakpoint would happily split an 840px pane on a 1600px monitor.
 */
const MASTER_DETAIL_MIN_PX = 940;

export function FocusPaneHost() {
  const { t } = useTranslation();
  const paneId = useUIStore((s) => s.focusPane);
  const closePane = useUIStore((s) => s.closeFocusPane);
  const widthPct = useUIStore((s) => s.focusPaneWidthPct);
  const setWidthPct = useUIStore((s) => s.setFocusPaneWidthPct);
  const tasklistGate = useTasklistGate();
  const viewport = useViewportSize();
  const isCompact = isCompactViewport(viewport);

  // §5.6 chat-left invariant. The focus-pane model puts a working surface on
  // the right and the CHAT on the left, but opening a pane never navigated —
  // so a full-page route (e.g. `/agent`) stayed mounted on the left, squeezed
  // beside the pane. Enforce here, in the ONE host, so it holds for every pane
  // and every opener (rail clicks AND the concierge signalling hooks).
  const location = useLocation();
  const navigate = useNavigate();
  const selection = useActiveContextStore((s) => s.selection);
  const selectionRef = useRef(selection);
  selectionRef.current = selection;
  const prevPaneRef = useRef(paneId);
  const prevPathRef = useRef(location.pathname);
  const prevCompactRef = useRef(isCompact);

  // Hold the surface across the close animation so the slide-out is visible.
  const [mountedId, setMountedId] = useState(paneId);
  const [isOpen, setIsOpen] = useState(paneId !== null);

  useEffect(() => {
    if (paneId) {
      setMountedId(paneId);
      const id = requestAnimationFrame(() => setIsOpen(true));
      return () => cancelAnimationFrame(id);
    }
    setIsOpen(false);
    const id = window.setTimeout(() => setMountedId(null), OPEN_CLOSE_MS);
    return () => window.clearTimeout(id);
  }, [paneId]);

  // Container-aware layout: measure OUR width and pass the verdict down.
  const asideRef = useRef<HTMLElement | null>(null);
  const [paneWidth, setPaneWidth] = useState(0);
  useEffect(() => {
    const el = asideRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      // Ignore the 0-width frames of the slide, else the surface would flip to
      // single-column every time the pane opens.
      if (w > 0) setPaneWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [mountedId]);

  useEffect(() => {
    if (!paneId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closePane();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [paneId, closePane]);

  // A gated-off surface has nothing to show — close rather than render an
  // empty pane the lawyer cannot act on.
  useEffect(() => {
    if (paneId === "tasks" && tasklistGate === "disabled") closePane();
  }, [paneId, tasklistGate, closePane]);

  // §5.6 chat-left invariant — two directions, one rule (`paneId !== null` ⇒ a
  // chat-left route). Keyed on `paneId` (the store value, NOT `mountedId` which
  // lingers through the close animation) + the pathname.
  useEffect(() => {
    const prevPane = prevPaneRef.current;
    const prevPath = prevPathRef.current;
    const prevCompact = prevCompactRef.current;
    prevPaneRef.current = paneId;
    prevPathRef.current = location.pathname;
    prevCompactRef.current = isCompact;

    if (!paneId) return;
    if (isChatLeftRoute(location.pathname)) return;

    // A pane is open beside a full-page route — reconcile by CAUSE.
    const paneJustOpened = prevPane === null;
    // A compact viewport skips the navigation (the pane is a full-screen
    // overlay, no left column). If it later grows to non-compact, the overlay
    // becomes a side pane and the full-page route WOULD get squeezed on the
    // left — so treat the un-compact transition as a fresh open.
    const justUncompacted = prevCompact && !isCompact;

    if (paneJustOpened || justUncompacted) {
      // Direction 1: put the chat on the left. Skip while compact (overlay).
      // Push so browser-back returns here.
      if (!isCompact) navigate(chatTargetForContext(selectionRef.current));
      return;
    }
    if (prevPath !== location.pathname) {
      // Direction 2: navigated to a full-page route under an open pane → the
      // lawyer chose a destination that owns the viewport; close the pane.
      closePane();
    }
  }, [paneId, location.pathname, isCompact, navigate, closePane]);

  if (!mountedId) return null;
  if (mountedId === "tasks" && tasklistGate === "disabled") return null;

  const surface = FOCUS_PANE_SURFACES[mountedId];
  // Before the first measurement assume narrow: a single column that widens is
  // a smaller glitch than a squeezed detail that snaps away.
  const narrow =
    isCompact || paneWidth === 0 ? true : paneWidth < MASTER_DETAIL_MIN_PX;

  const body = (
    <div className="relative flex h-full min-h-0 flex-col">
      {surface.bareHeader ? (
        // No title bar — the surface owns its heading. When it ALSO owns the
        // close (the Agent hero relocated it into its cluster — W-1), the host
        // renders none, so there is exactly one close. Otherwise float a close
        // so the pane stays dismissable and the surface starts at the very top.
        surface.ownsClose ? null : (
          <button
            type="button"
            data-testid="focus-pane-close"
            onClick={closePane}
            aria-label={t("common.close")}
            title={t("common.close")}
            className="absolute right-2 top-2 z-10 rounded p-1 text-slate-400 hover:bg-cream-100 hover:text-slate-600 dark:hover:bg-coffee-800"
          >
            <X size={16} />
          </button>
        )
      ) : (
        <div className="flex shrink-0 items-center gap-2 border-b border-cream-200/60 px-4 py-3 dark:border-coffee-800/60">
          <h2 className="flex-1 font-serif-display text-lg font-semibold text-slate-800 dark:text-slate-100">
            {t(surface.titleKey)}
          </h2>
          <button
            type="button"
            data-testid="focus-pane-close"
            onClick={closePane}
            aria-label={t("common.close")}
            title={t("common.close")}
            className="rounded p-1 text-slate-400 hover:bg-cream-100 hover:text-slate-600 dark:hover:bg-coffee-800"
          >
            <X size={16} />
          </button>
        </div>
      )}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {surface.render({ narrow })}
      </div>
    </div>
  );

  if (isCompact) {
    return (
      <div
        data-testid="focus-pane-overlay"
        data-pane={mountedId}
        className="fixed inset-0 z-50 flex flex-col bg-white dark:bg-coffee-900"
      >
        {body}
      </div>
    );
  }

  return (
    <aside
      ref={asideRef}
      data-testid="focus-pane"
      data-pane={mountedId}
      data-state={isOpen ? "open" : "closed"}
      style={{ width: isOpen ? `${widthPct}vw` : 0 }}
      className="relative flex h-full shrink-0 overflow-hidden border-l border-cream-300/70 bg-white transition-[width] duration-200 ease-out dark:border-coffee-700 dark:bg-coffee-900"
    >
      {/* Handle on the pane's LEFT edge. `side="left"` already flips the
          sign, so a positive delta means "widen the pane". Width is a % of the
          viewport, and the store clamps to the 35–65% band. */}
      <ResizeHandle
        side="left"
        onResize={(delta) =>
          setWidthPct(widthPct + (delta / window.innerWidth) * 100)
        }
        ariaLabel={t("common.resizePane")}
        ariaValueNow={Math.round(widthPct)}
        ariaValueMin={FOCUS_PANE_MIN_PCT}
        ariaValueMax={FOCUS_PANE_MAX_PCT}
        testid="focus-pane-resize"
      />
      <div className="min-w-0 flex-1">{body}</div>
    </aside>
  );
}
